/**
 * @file FeedbackBox.tsx
 *
 * @description A component that allows police officers to provide feedback on reports
 * in the internal web application. The component provides a user-friendly interface
 * for rating various aspects of reports and submitting additional text feedback.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 * @requires js-cookie
 */

import {
  Feedback,
  SentimentNeutral,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
  CheckCircle,
} from "@mui/icons-material";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import Grid from "@mui/material/Grid";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import { rmsClient } from "../../api/clients";

/**
 * Enum representing the possible feedback ratings an officer can provide
 * @enum {number}
 */
enum FeedbackRating {
  POOR = 1,
  FAIR = 2,
  GOOD = 3,
}

/**
 * Interface representing the structure of feedback data
 * @interface Feedback
 */
interface FeedbackData {
  /** Unique identifier for the report being rated */
  reportId: string;
  /** Optional additional text feedback from the officer */
  feedback: string;
  /** Rating for the overall accuracy of the report */
  overallAccuracyRating: FeedbackRating | null;
  /** Rating for the level of detail in the report */
  overallDetailRating: FeedbackRating | null;
  /** Rating for the overall user experience */
  overallUserExperienceRating: FeedbackRating | null;
  /** Rating for the report editing experience */
  editingExperienceRating: FeedbackRating | null;
  /** Rating for the quality of the narrative in the report */
  narrativeQualityRating: FeedbackRating | null;
  /** Rating for the clarity of the narrative in the report */
  narrativeClarityRating: FeedbackRating | null;
  /** Rating for the report generation experience */
  generationExperienceRating: FeedbackRating | null;
}

/**
 * Mapping of feedback field keys to human-readable labels
 */
const FeedbackFieldAliases: Record<keyof FeedbackData, string> = {
  reportId: "Report ID",
  feedback: "Additional Feedback",
  overallAccuracyRating: "Report Accuracy",
  overallDetailRating: "Report Detail",
  overallUserExperienceRating: "User Experience",
  editingExperienceRating: "Editing Experience",
  narrativeQualityRating: "Narrative Quality",
  narrativeClarityRating: "Narrative Clarity",
  generationExperienceRating: "Generation Experience",
};

/**
 * Tooltip text for each rating level
 */
const RatingTooltips = {
  [FeedbackRating.GOOD]: "Good",
  [FeedbackRating.FAIR]: "Fair",
  [FeedbackRating.POOR]: "Poor",
};

/**
 * Props for the FeedbackBox component
 * @interface FeedbackBoxProps
 */
interface FeedbackBoxProps {
  /** The ID of the report for which feedback is being provided */
  reportID: string;
}

/**
 * @name FeedbackBox
 *
 * @description A component that allows officers to provide feedback on reports
 *`
 * @param {FeedbackBoxProps} props - The component props
 *
 * @returns {JSX.Element} The rendered FeedbackBox component
 */
export const FeedbackBox: React.FC<FeedbackBoxProps> = ({ reportID }) => {
  const theme = useTheme();

  /**
   * State to track the feedback data being collected
   */
  const [feedback, setFeedback] = useState<FeedbackData>({
    reportId: reportID,
    feedback: "",
    overallAccuracyRating: null,
    overallDetailRating: null,
    overallUserExperienceRating: null,
    editingExperienceRating: null,
    narrativeQualityRating: null,
    narrativeClarityRating: null,
    generationExperienceRating: null,
  });
  // State to track whether feedback has been successfully submitted
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  // State to track whether feedback is currently being submitted
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Custom hook to handle error and success snackbar messages
  const {
    showSuccess,
    showError,
    snackBarMessage,
    snackBarMode,
    handleCloseSnackbar,
  } = useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();

  /**
   * @name handleSelectionChange
   *
   * @description Updates the feedback state when a rating is selected
   *
   * @param {FeedbackRating | null} value - The selected rating value
   * @param {keyof FeedbackData} feedbackKey - The feedback field to update
   */
  const handleSelectionChange = (
    value: FeedbackRating | null,
    feedbackKey: keyof FeedbackData
  ) => {
    setFeedback((prevFeedback) => ({
      ...prevFeedback,
      [feedbackKey]: value,
    }));
  };

  /**
   * @name validateFeedback
   *
   * @description Validates the feedback data before submission
   *
   * @returns {boolean} Whether the feedback is valid for submission
   */
  const validateFeedback = (): boolean => {
    if (!feedback.reportId) {
      showError("Report ID is required to submit feedback.");
      return false;
    }
    if (feedback.feedback.length > 500) {
      showError("Feedback text cannot exceed 500 characters.");
      return false;
    }
    // make sure at least one rating is provided
    const feedbackKeys = Object.keys(feedback).filter(
      (key) => key !== "reportId"
    ) as Array<keyof FeedbackData>;
    if (
      feedbackKeys.every(
        (key) => feedback[key] === null || feedback[key] === ""
      )
    ) {
      showError("At least one rating must be provided.");
      return false;
    }

    // Check if all ratings are valid
    const ratingKeys = feedbackKeys.filter((key) => key !== "feedback");
    const validRatings = Object.values(FeedbackRating);
    if (
      !ratingKeys.every(
        (key) => feedback[key] === null || validRatings.includes(feedback[key])
      )
    ) {
      showError("All ratings must be either poor, fair, or good.");
      return false;
    }
    return true;
  };

  /**
   * @name handleFeedbackSubmit
   *
   * @description Handles the asynchronous submission of feedback to the backend API
   */
  const handleFeedbackSubmit = async () => {
    if (!validateFeedback() || feedbackSubmitted || feedbackSubmitting) {
      return;
    }
    setFeedbackSubmitting(true);
    rmsClient
      .post("/report/feedback", feedback)
      .then(() => {
        setFeedback({
          reportId: reportID,
          feedback: "",
          overallAccuracyRating: null,
          overallDetailRating: null,
          overallUserExperienceRating: null,
          editingExperienceRating: null,
          narrativeQualityRating: null,
          narrativeClarityRating: null,
          generationExperienceRating: null,
        });
        showSuccess("Feedback submitted successfully.");
        setFeedbackSubmitted(true);
      })
      .catch((error) => {
        showError(
          error.response?.data?.error ||
            "An error occurred while submitting feedback. Please try again later."
        );
      })
      .finally(() => {
        setFeedbackSubmitting(false);
      });
  };

  /**
   * @name handleTextFeedbackChange
   *
   * @description Updates the text feedback state while enforcing character limit
   *
   * @param {string} value - The new feedback text value
   */
  const handleTextFeedbackChange = (value: string) => {
    if (value.length <= 500) {
      setFeedback((prevFeedback) => ({
        ...prevFeedback,
        feedback: value,
      }));
    }
  };

  /**
   * @name renderRatingIcon
   *
   * @description Renders a single rating icon with consistent styling
   */
  const renderRatingIcon = (
    Icon: typeof SentimentVerySatisfied,
    rating: FeedbackRating,
    key: keyof FeedbackData,
    activeColor: string
  ) => {
    const isSelected = feedback[key] === rating;
    return (
      <Tooltip title={RatingTooltips[rating]}>
        <Box
          onClick={() => handleSelectionChange(rating, key)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "50%",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            bgcolor: isSelected ? alpha(activeColor, 0.12) : "transparent",
            "&:hover": {
              bgcolor: alpha(activeColor, 0.08),
              transform: "scale(1.15)",
            },
          }}
        >
          <Icon
            sx={{
              fontSize: 28,
              color: isSelected ? activeColor : theme.palette.action.disabled,
              transition: "color 0.2s ease-in-out",
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  /**
   * @name renderRatingSelect
   *
   * @description Renders the rating selection UI for all feedback categories
   *
   * @returns {JSX.Element} The rendered rating selection UI
   */
  const renderRatingSelect = () => {
    const feedbackKeys = Object.keys(feedback).filter(
      (val) => !["feedback", "reportId"].includes(val)
    ) as Array<keyof FeedbackData>;

    return (
      <Grid container spacing={2}>
        {feedbackKeys.map((key) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  mb: 1,
                  textAlign: "center",
                }}
              >
                {FeedbackFieldAliases[key]}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {renderRatingIcon(
                  SentimentVerySatisfied,
                  FeedbackRating.GOOD,
                  key,
                  theme.palette.success.main
                )}
                {renderRatingIcon(
                  SentimentNeutral,
                  FeedbackRating.FAIR,
                  key,
                  theme.palette.warning.main
                )}
                {renderRatingIcon(
                  SentimentVeryDissatisfied,
                  FeedbackRating.POOR,
                  key,
                  theme.palette.error.main
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  // If feedback is already submitted, render a success message
  if (feedbackSubmitted) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mt: 4,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(theme.palette.success.main, 0.08),
          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          maxWidth: 500,
          mx: "auto",
        }}
      >
        <CheckCircle
          sx={{
            fontSize: 48,
            color: theme.palette.success.main,
            mb: 1.5,
          }}
        />
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.success.dark }}
        >
          Thank you for your feedback!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Your input helps improve the system for all officers.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        mt: 4,
        borderRadius: 2,
        width: "100%",
        maxWidth: 700,
        mx: "auto",
        border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.2),
        },
      }}
    >
      <SnackBarMessage
        message={snackBarMessage}
        mode={snackBarMode}
        handleClose={handleCloseSnackbar}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <Feedback sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Report Feedback
        </Typography>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, textAlign: "center" }}
      >
        Your feedback helps us improve the system for all officers. Please rate
        any aspects you'd like to comment on.
      </Typography>

      {/* Rating Grid */}
      {renderRatingSelect()}

      {/* Text Feedback */}
      <Box sx={{ mt: 3 }}>
        <TextField
          variant="outlined"
          multiline
          label="Additional Feedback"
          placeholder="Share any additional thoughts or suggestions..."
          value={feedback.feedback}
          onChange={(e) => handleTextFeedbackChange(e.target.value)}
          rows={3}
          helperText={`${feedback.feedback.length}/500`}
          fullWidth
          InputProps={{
            sx: { borderRadius: 2 },
          }}
        />
      </Box>

      {/* Submit Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          variant="contained"
          startIcon={
            feedbackSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Feedback />
            )
          }
          onClick={handleFeedbackSubmit}
          loading={feedbackSubmitting}
          disabled={feedbackSubmitted || feedbackSubmitting}
          sx={{
            py: 1.5,
            px: 4,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "none",
            boxShadow: `0 4px 14px ${alpha(
              theme.palette.primary.main,
              0.35
            )}`,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 6px 20px ${alpha(
                theme.palette.primary.main,
                0.45
              )}`,
            },
          }}
        >
          {feedbackSubmitting ? "Submitting..." : "Submit Feedback"}
        </Button>
      </Box>
    </Paper>
  );
};
